import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {WorkspaceStatus} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import {getOttopilotWorkspacePath} from "~/utilities/ottopilotRoutes";
import {configDashboardHref} from "~/workspaceConfig/configHref";
import {resolveConfigFormHref} from "~/workspaceConfig/hostedForms/hostedFormHref";

export const workspaceStatusToDisplayName: Record<WorkspaceStatus, string> = {
    [WorkspaceStatus.active]: "Active",
    [WorkspaceStatus.deleted]: "Deleted",
};

export function getWorkspaceInitials(displayName: string): string {
    const words = displayName.trim().split(/\s+/).filter((word) => word.length > 0);
    if (words.length >= 2) {
        return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`.toUpperCase();
    }
    return words[0]?.slice(0, 2).toUpperCase() ?? "";
}

export type WorkspaceBillingInformation = {
    billingFirstName: string | null;
    billingLastName: string | null;
    billingCountryId: string | null;
    billingEmail: string | null;
    billingPhoneNumber: string | null;
    billingAddress: string | null;
    billingCompanyLegalName: string | null;
    billingVatNumber: string | null;
    billingGstNumber: string | null;
};

export function resolveWorkspaceMobileBottomHomeHref(workspaceId: Uuid, mobileBottomHomeHref: string | undefined): string | undefined {
    if (mobileBottomHomeHref === undefined || mobileBottomHomeHref === "") {
        return undefined;
    }

    if (mobileBottomHomeHref === "dashboard" || mobileBottomHomeHref === "/dashboard") {
        return configDashboardHref(workspaceId);
    }

    if (mobileBottomHomeHref.startsWith(`/w/${workspaceId}/`)) {
        return mobileBottomHomeHref;
    }

    const relative = mobileBottomHomeHref.replace(/^\/+/, "");
    if (relative === "btl-activities" || relative.startsWith("btl-activities/") || relative === "btl-activity-templates" || relative.startsWith("btl-activity-templates/")) {
        return getOttopilotWorkspacePath(workspaceId, relative);
    }

    return relative.startsWith("w/") ? `/${relative}` : `/w/${workspaceId}/${relative}`;
}

export function workspaceCreateItemsWithEventContainerId(
    items: readonly WorkspaceMobileBottomCreateItem[],
    eventContainerId: Uuid | null | undefined,
): readonly WorkspaceMobileBottomCreateItem[] {
    if (eventContainerId === null || eventContainerId === undefined) {
        return items;
    }

    return items.map((item) => ({
        ...item,
        formQueryParams: {
            ...(item.formQueryParams ?? {}),
            eventContainerId: String(eventContainerId),
        },
    }));
}

export function buildWorkspaceMobileBottomCreateHref(workspaceId: Uuid, item: Pick<WorkspaceMobileBottomCreateItem, "formUrl" | "formQueryParams" | "formId">): string {
    const configHref = resolveConfigFormHref({
        workspaceId,
        formId: item.formId,
        search: item.formQueryParams,
    });
    if (configHref !== null) {
        return configHref;
    }
    const isLeftoverBtlForm =
        item.formUrl === "btl-activities"
        || item.formUrl === "btl-activity-templates"
        || item.formUrl.startsWith("btl-activities/")
        || item.formUrl.startsWith("btl-activity-templates/");
    if (isLeftoverBtlForm !== true && item.formUrl !== "user-invitation") {
        throw new Error(`No config form href for mobile create item ${item.formUrl}`);
    }
    const base = item.formUrl === "user-invitation" ? `/w/${workspaceId}/user-invitation` : getOttopilotWorkspacePath(workspaceId, item.formUrl);
    const params = {...item.formQueryParams};

    if (item.formUrl === "user-invitation" && params.view === undefined) {
        params.view = "create";
    }

    if (item.formUrl === "btl-activity-templates" && params.view === undefined) {
        params.view = "create";
    }

    if (Object.keys(params).length === 0) {
        return base;
    }

    return `${base}?${new URLSearchParams(params).toString()}`;
}
